import { useMemo, useState } from "react";
import axios from "axios";
import { useProjectClosureConfirmation } from "@/components/ProjectClosureDialog";
import { useProjectCompletionConfirmation } from "@/components/ProjectCompletionDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { lifecycleStageChoices } from "@/utils/lifecycleStages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export default function ProjectQuickStatusEditor({ canAccessBilling = false, onClose, onSaved, project }) {
  const stages = useMemo(
    () => lifecycleStageChoices(project, { canAccessBilling }),
    [project, canAccessBilling],
  );
  const preferredStage = project.lifecycle_state || project.official_status || project.operational_status;
  const isReopen = preferredStage === "closed";
  const defaultStage =
    stages.some(([value]) => value === preferredStage)
      ? preferredStage
      : isReopen && stages.some(([value]) => value === "in_progress")
        ? "in_progress"
        : (stages[0]?.[0] || "not_started");
  const [stage, setStage] = useState(defaultStage);
  const [blocked, setBlocked] = useState(Boolean(project.operational_blocker));
  const [blockerReason, setBlockerReason] = useState(project.operational_blocker_reason || "");
  const [reopenReason, setReopenReason] = useState("");
  const [saving, setSaving] = useState(false);
  const { requestCompletion, completionDialog } = useProjectCompletionConfirmation();
  const { requestClosure, closureDialog, closureBusy } = useProjectClosureConfirmation(`${project.id}:${stage}`);

  const trimmedReopenReason = reopenReason.trim();
  const trimmedBlockerReason = blockerReason.trim();
  const saveDisabled =
    saving || closureBusy ||
    (isReopen && !trimmedReopenReason) ||
    (stage === "blocked" && !trimmedBlockerReason);

  const save = async (confirmed = false) => {
    if (stage === "closed") {
      requestClosure({
        onError: async (error) => {
          if (error?.response?.status === 409) {
            await onSaved();
          }
        },
        loadPreview: async () => (await axios.get(`${API}/projects/${project.id}/closure-preview`)).data,
        close: async (preview) => {
          setSaving(true);
          try {
            await axios.post(`${API}/projects/${project.id}/lifecycle`, {
              lifecycle_state: "closed", notes: "", confirmed: true,
              closure_preview_token: preview.preview_token,
            });
            toast.success("Project closed.");
            await onSaved();
            onClose();
          } finally { setSaving(false); }
        },
      });
      return;
    }
    if (stage === "complete" && confirmed !== true) {
      requestCompletion(() => save(true));
      return;
    }
    setSaving(true);
    try {
      // Completion uses the dedicated, explicitly confirmed lifecycle endpoint.
      if (stage === "complete") {
        await axios.post(`${API}/projects/${project.id}/lifecycle`, {
          lifecycle_state: stage,
          notes: "",
          confirmed: true,
        });
        toast.success("Project completion recorded.");
        await onSaved();
        onClose();
        return;
      }
      const lifecyclePath = canAccessBilling ? "lifecycle/override" : "lifecycle";
      // If reopening, `reason` carries the reopen reason. When target is
      // Blocked, prefer the blocker reason (it also satisfies the reopen
      // audit stamp on the backend).
      const reasonForServer =
        stage === "blocked"
          ? trimmedBlockerReason
          : isReopen
            ? trimmedReopenReason
            : "";
      await axios.post(`${API}/projects/${project.id}/${lifecyclePath}`, {
        lifecycle_state: stage,
        reason: reasonForServer,
      });
      if (blocked && !project.operational_blocker) await axios.post(`${API}/projects/${project.id}/blocker/create`, { reason: trimmedBlockerReason });
      if (blocked && project.operational_blocker && trimmedBlockerReason !== (project.operational_blocker_reason || "")) await axios.patch(`${API}/projects/${project.id}/blocker`, { reason: trimmedBlockerReason });
      if (!blocked && project.operational_blocker) await axios.post(`${API}/projects/${project.id}/blocker/resolve`);
      toast.success(isReopen ? "Project reopened." : "Project status updated.");
      await onSaved();
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : (detail?.message || "Project status could not be updated.");
      toast.error(message);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="project-quick-status-editor">
        <DialogHeader>
          <DialogTitle>{isReopen ? "Reopen Project" : "Quick Project Stage"}</DialogTitle>
          <DialogDescription>
            {isReopen
              ? "Pick the stage to move this closed project into and briefly explain why it's coming back — the reason is written to the audit trail."
              : "Update the Project Stage or mark this project as Roadblocked."}
          </DialogDescription>
        </DialogHeader>
        {isReopen && (
          <label htmlFor="quick-status-reopen-reason-input" style={{ display: "block" }}>
            Reopen reason <span style={{ color: "#b91c1c" }}>*</span>
            <input
              id="quick-status-reopen-reason-input"
              data-testid="quick-status-reopen-reason-input"
              onChange={(event) => setReopenReason(event.target.value)}
              value={reopenReason}
              placeholder="e.g. Customer requested additional work"
              autoFocus
            />
          </label>
        )}
        <label htmlFor="quick-status-stage-select">
          Project Stage
          <select id="quick-status-stage-select" data-testid="quick-status-stage-select" onChange={(event) => setStage(event.target.value)} value={stage}>
            {stages.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label htmlFor="quick-status-blocked-input">
          <input id="quick-status-blocked-input" checked={blocked} data-testid="quick-status-blocked-input" onChange={(event) => setBlocked(event.target.checked)} type="checkbox" />
          Roadblocked
        </label>
        {blocked && (
          <label htmlFor="quick-status-blocker-reason-input">
            Blocker reason
            <input
              data-testid="quick-status-blocker-reason-input"
              id="quick-status-blocker-reason-input"
              onChange={(event) => setBlockerReason(event.target.value)}
              value={blockerReason}
            />
          </label>
        )}
        <DialogFooter>
          <Button data-testid="quick-status-cancel" onClick={onClose} type="button" variant="ghost">Cancel</Button>
          <Button data-testid="quick-status-save" disabled={saveDisabled} onClick={save} type="button">
            {saving ? "Saving…" : isReopen ? "Reopen project" : "Save"}
          </Button>
        </DialogFooter>
        {completionDialog}
        {closureDialog}
      </DialogContent>
    </Dialog>
  );
}
